/**
 * Marketing Dashboard tab — customizable widget-based dashboard.
 * Users can create dashboards from templates, add/remove widgets,
 * drag-and-drop to rearrange, and persist layouts.
 */

import { useEffect, useState, useCallback, useMemo } from 'react'
import { Card, CardContent, Spinner } from '@/components/ui'
import { Button } from '@/components/ui'
import { Sparkles, RefreshCw } from 'lucide-react'
import { useMarketing } from '@/hooks/useMarketing'
import { useMarketingStore } from '@/stores/marketingStore'
import { useDashboard } from '@/hooks/useDashboard'
import { useToast } from '@/components/ui/Toast'
import { DashboardToolbar } from '@/components/marketing/dashboard/DashboardToolbar'
import { DashboardCanvas } from '@/components/marketing/dashboard/DashboardCanvas'
import { WidgetCatalog } from '@/components/marketing/dashboard/WidgetCatalog'
import { TemplatePicker } from '@/components/marketing/dashboard/TemplatePicker'
import { getWidgetEntry } from '@/components/marketing/dashboard/widgetRegistry'
import { resolveFetchKinds } from '@/components/marketing/dashboard/widgetTransforms'
import type { FetchKind } from '@/components/marketing/dashboard/widgetTransforms'
import type { WidgetDataBag } from '@/components/marketing/dashboard/WidgetRenderer'
import type { WidgetRegistryEntry, DashboardTemplate, WidgetDataSource } from '@/types/dashboard'

export function MarketingDashboard() {
  const {
    fetchStatus,
    fetchOverview,
    fetchCampaigns,
    fetchCampaignPerformance,
    fetchAttribution,
    fetchTimeSeries,
    fetchOptimizationActions,
    fetchConversions,
    fetchAudiences,
    reviewOptimizationAction,
    runOptimization,
    hasAccount,
    overview,
    campaigns,
    campaignPerformance,
    attributionFunnel,
    attributionUnattributed,
    timeSeries,
    optimizationActions,
    conversionEvents,
  } = useMarketing()

  const {
    currentDashboard,
    dashboards,
    isEditMode,
    isDirty,
    fetchDashboards,
    fetchDashboard,
    createDashboard,
    saveDashboard,
    setEditMode,
    addWidget,
    removeWidget,
    updateLayouts,
    updateGlobalDateRange,
  } = useDashboard()

  const { addToast } = useToast()

  const [loading, setLoading] = useState(true)
  const [dataLoading, setDataLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [optimizing, setOptimizing] = useState(false)
  const [catalogOpen, setCatalogOpen] = useState(false)
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false)
  const [accountChecked, setAccountChecked] = useState(false)

  // ── Determine which data sources the current dashboard needs ──

  const requiredSources = useMemo<Set<WidgetDataSource>>(() => {
    const sources = new Set<WidgetDataSource>()
    if (!currentDashboard) return sources
    for (const widget of currentDashboard.widgets) {
      const entry = getWidgetEntry(widget.type)
      for (const ds of entry.dataSources) sources.add(ds)
    }
    return sources
  }, [currentDashboard])

  // ── Fetch data based on required sources ──

  const fetchData = useCallback(async (dateRange: number) => {
    setDataLoading(true)
    try {
      // Exhaustive over FetchKind — a widget data source without a fetcher
      // here is a type error, not a silently empty widget.
      const fetchers: Record<FetchKind, () => Promise<unknown>> = {
        overview: () => fetchOverview(dateRange),
        campaigns: () => fetchCampaigns(),
        campaignPerformance: () => fetchCampaignPerformance(dateRange),
        attribution: () => fetchAttribution(dateRange),
        timeSeries: () => fetchTimeSeries(dateRange),
        optimizationActions: () => fetchOptimizationActions('recommended'),
        conversions: () => fetchConversions(),
        audiences: () => fetchAudiences(),
      }
      // allSettled: one failing source (e.g. a 403) must not tear down the
      // loading state or discard the other widgets' data.
      await Promise.allSettled([...resolveFetchKinds(requiredSources)].map((kind) => fetchers[kind]()))
    } catch {
      // data will be empty
    } finally {
      setDataLoading(false)
    }
  }, [requiredSources, fetchOverview, fetchCampaigns, fetchCampaignPerformance, fetchAttribution, fetchTimeSeries, fetchOptimizationActions, fetchConversions, fetchAudiences])

  // ── Initial load: check account, fetch dashboards ──

  useEffect(() => {
    let cancelled = false
    async function init() {
      setLoading(true)
      try {
        const status = await fetchStatus()
        if (!cancelled) setAccountChecked(true)
        if (!status.hasAccount) return

        const list = await fetchDashboards()
        if (cancelled) return

        if (list.length > 0) {
          await fetchDashboard(list[0].id)
        } else {
          setTemplatePickerOpen(true)
        }
      } catch {
        // silently handle
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    init()
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Refetch data whenever dashboard, date range, channel filter, or
  //    campaign selection changes ──

  const selectedChannel = useMarketingStore((s) => s.selectedChannel)
  const selectedCampaignIds = useMarketingStore((s) => s.selectedCampaignIds)

  // One shared range across marketing surfaces: the store is the live value
  // (shown in the toolbar, used for every fetch); the dashboard's persisted
  // globalDateRange only *seeds* it, so it never clobbers a range the user
  // explicitly picked on another tab. This effect must run before the fetch
  // effect below (same commit on dashboard load).
  const days = useMarketingStore((s) => s.dateRangeDays)
  const setDateRangeDays = useMarketingStore((s) => s.setDateRangeDays)
  const seedDateRangeDays = useMarketingStore((s) => s.seedDateRangeDays)
  useEffect(() => {
    if (currentDashboard?.globalDateRange) seedDateRangeDays(currentDashboard.globalDateRange)
  }, [currentDashboard?.id, currentDashboard?.globalDateRange, seedDateRangeDays])

  useEffect(() => {
    if (!currentDashboard || !hasAccount) return
    // Read at call time: the seed effect above may have just updated the store
    // in this same commit, and this avoids a second fetch on load.
    fetchData(useMarketingStore.getState().dateRangeDays)
  }, [currentDashboard?.id, currentDashboard?.globalDateRange, currentDashboard?.widgets.length, hasAccount, selectedChannel, selectedCampaignIds]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──

  const handleSelectTemplate = useCallback(async (template: DashboardTemplate) => {
    try {
      const widgets = template.widgets.map((w) => ({
        ...w,
        id: crypto.randomUUID(),
      }))
      await createDashboard({
        name: template.name,
        description: template.description,
        templateId: template.id,
        widgets,
        globalDateRange: template.globalDateRange,
      })
      await fetchDashboards()
      addToast({ title: 'Dashboard created', description: `"${template.name}" dashboard is ready`, type: 'success' })
    } catch (err) {
      addToast({ title: 'Error', description: String(err), type: 'error' })
    }
  }, [createDashboard, fetchDashboards, addToast])

  const handleCreateBlank = useCallback(async () => {
    try {
      await createDashboard({ name: 'My Dashboard' })
      await fetchDashboards()
      setEditMode(true)
      addToast({ title: 'Dashboard created', description: 'Start adding widgets to your dashboard', type: 'success' })
    } catch (err) {
      addToast({ title: 'Error', description: String(err), type: 'error' })
    }
  }, [createDashboard, fetchDashboards, setEditMode, addToast])

  const handleAddWidget = useCallback((entry: WidgetRegistryEntry) => {
    addWidget({
      id: crypto.randomUUID(),
      type: entry.type,
      title: entry.name,
      layout: {
        x: 0,
        y: Infinity, // append at bottom
        w: entry.sizeConstraints.defaultW,
        h: entry.sizeConstraints.defaultH,
      },
      config: Object.fromEntries(
        entry.configFields.map((f) => [f.key, f.defaultValue]),
      ),
    })
  }, [addWidget])

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      await saveDashboard()
      addToast({ title: 'Saved', description: 'Dashboard layout saved', type: 'success' })
    } catch (err) {
      addToast({ title: 'Error', description: String(err), type: 'error' })
    } finally {
      setSaving(false)
    }
  }, [saveDashboard, addToast])

  const handleSwitchDashboard = useCallback(async (id: string) => {
    try {
      await fetchDashboard(id)
    } catch (err) {
      addToast({ title: 'Error', description: String(err), type: 'error' })
    }
  }, [fetchDashboard, addToast])

  const handleReviewAction = useCallback(async (actionId: string, decision: 'approve' | 'dismiss') => {
    try {
      await reviewOptimizationAction(actionId, { action: decision })
    } catch (err) {
      addToast({ title: 'Error', description: String(err), type: 'error' })
    }
  }, [reviewOptimizationAction, addToast])

  const handleRunOptimization = useCallback(async () => {
    setOptimizing(true)
    try {
      const count = await runOptimization()
      await fetchOptimizationActions('recommended')
      addToast({
        title: 'Optimization complete',
        description: count > 0
          ? `Found ${count} optimization recommendation${count !== 1 ? 's' : ''}`
          : 'No new recommendations at this time',
        type: 'success',
      })
    } catch (err) {
      addToast({ title: 'Error', description: String(err), type: 'error' })
    } finally {
      setOptimizing(false)
    }
  }, [runOptimization, fetchOptimizationActions, addToast])

  const handleRefresh = useCallback(async () => {
    if (!currentDashboard) return
    await fetchData(useMarketingStore.getState().dateRangeDays)
  }, [currentDashboard, fetchData])

  // ── Build data bag for widgets ──

  const widgetData: WidgetDataBag = useMemo(() => ({
    overview,
    campaigns,
    campaignPerformance,
    attributionFunnel,
    attributionUnattributed,
    timeSeries,
    optimizationActions,
    conversions: conversionEvents,
    dateRangeDays: days,
    isLoading: dataLoading,
  }), [overview, campaigns, campaignPerformance, attributionFunnel, attributionUnattributed, timeSeries, optimizationActions, conversionEvents, days, dataLoading])

  // ── Render ──

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (accountChecked && !hasAccount) {
    return (
      <Card>
        <CardContent className="py-12 text-center space-y-3">
          <p className="text-muted-foreground">No Meta Ad Account connected.</p>
          <p className="text-sm text-muted-foreground">
            Go to the <strong>Settings</strong> tab to connect your Meta Ad Account and start managing campaigns.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Template picker (shown when no dashboards exist) */}
      <TemplatePicker
        open={templatePickerOpen}
        onOpenChange={setTemplatePickerOpen}
        onSelectTemplate={handleSelectTemplate}
        onCreateBlank={handleCreateBlank}
      />

      {/* Widget catalog (add widget modal) */}
      <WidgetCatalog
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        onAddWidget={handleAddWidget}
      />

      {currentDashboard && (
        <>
          {/* Toolbar row */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <DashboardToolbar
                dashboardName={currentDashboard.name}
                globalDateRange={days}
                isEditMode={isEditMode}
                isDirty={isDirty}
                isSaving={saving}
                dashboards={dashboards}
                currentDashboardId={currentDashboard.id}
                onToggleEditMode={() => setEditMode(!isEditMode)}
                onOpenCatalog={() => setCatalogOpen(true)}
                onSave={handleSave}
                onDateRangeChange={(d) => { setDateRangeDays(d); updateGlobalDateRange(d) }}
                onSwitchDashboard={handleSwitchDashboard}
                onNewDashboard={() => setTemplatePickerOpen(true)}
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRunOptimization}
                disabled={optimizing}
              >
                {optimizing ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {optimizing ? 'Analyzing...' : 'Optimize'}
              </Button>
              <Button variant="secondary" size="sm" onClick={handleRefresh}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Dashboard grid */}
          <DashboardCanvas
            widgets={currentDashboard.widgets}
            data={widgetData}
            isEditMode={isEditMode}
            rowHeight={currentDashboard.rowHeight}
            onLayoutChange={updateLayouts}
            onConfigureWidget={() => {}} // TODO: widget config panel
            onRemoveWidget={removeWidget}
            onReviewAction={handleReviewAction}
          />
        </>
      )}
    </div>
  )
}
